import { FC, useEffect, useRef } from 'react';

import { GameCommunicationController } from '@pp/core/src/controllers/gameCommunication/gameCommunicationController';
import type { GameCommunicationControllerConnectorProps, IGameCommunicationProcessorFactory } from '@pp/core/src/controllers/gameCommunication/types';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';

import { GameCommunicationMultiplierProcessor } from './controllers/gameCommunication/processors/GameCommunicationMultiplierProcessor';

const gameCommunicationProcessors: IGameCommunicationProcessorFactory[]  = [GameCommunicationMultiplierProcessor];
const gameCommunicationSenders: any[] = [];

export const BroadcasterCommunicationControllerConnector: FC<GameCommunicationControllerConnectorProps> = ({
  gameStore,
  config,
}): null => {

  const gameCommunicationControllerRef = useRef(new GameCommunicationController());

  const JSESSIONID = useConfigSlice.JSESSIONID();
  const tableId = useConfigSlice.tableId();
  const tableConfig = useConfigSlice.tableConfig();
  const isGamePaused = useGameStateSlice.getIsGamePaused();
  const gameChannel = useGameStateSlice.gameChannel();
  const isGameAbandoned = useGameStateSlice.getIsGameAbandoned();
  const isUserInfoReady = useGameStateSlice.userInfoReady();

  useEffect(() => {
    // Setting up Processors
    const processors = gameCommunicationProcessors.concat(config?.processors ?? []);
    processors.forEach((processor) => gameCommunicationControllerRef.current.setProcessor(processor, gameStore));

    // Setting up Senders
    const senders = gameCommunicationSenders.concat(config?.senders ?? []);
    // Redundant. Needed only due to invalid senders
    gameStore && gameCommunicationControllerRef.current.setStore(gameStore);
    senders.forEach((sender) => gameCommunicationControllerRef.current.setSender(sender));

    if (config?.connectionObserver) {
      gameCommunicationControllerRef.current.setConnectionObserver(config.connectionObserver);
    }
  }, [config, gameStore]);

  useEffect(() => {
    gameStore && gameCommunicationControllerRef.current.setStore(gameStore);
  }, [gameStore]);

  useEffect(() => {
    gameCommunicationControllerRef.current.stopStart(isGamePaused);
  }, [isGamePaused]);

  useEffect(() => {
    isGameAbandoned && gameCommunicationControllerRef.current.die();
  }, [isGameAbandoned]);

  useEffect(() => {
    gameChannel && gameCommunicationControllerRef.current.setInitialized();
  }, [gameChannel]);

  useEffect(() => {
    gameCommunicationControllerRef.current.setServerMessageDelay(0);
  }, []);

  useEffect(() => {
    if (tableConfig?.broadcasterWsAddress && JSESSIONID && tableId && isUserInfoReady) {
      gameCommunicationControllerRef.current.setCredentials(tableConfig.broadcasterWsAddress, JSESSIONID, tableId);
      gameCommunicationControllerRef.current.start();
    }
  }, [tableConfig, tableId, JSESSIONID, isUserInfoReady]);

  return null;
};
